import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Card,
  CardBody,
  CardHeader,
  Col,
  Row,
  Table,
  Badge,
  Button,
  Spinner,
} from 'reactstrap';
import { toast } from 'react-toastify';
import { dailyRecordsAPI } from '../../helpers/api';
import { useIsAdmin } from '../../helpers/hooks';
import AuthImage, { openAuthFile } from '../../components/Common/AuthImage';
import { useIsMobile } from '../../helpers/useMediaQuery';

interface DailyRecord {
  id: string;
  recordDate: string;
  isCompleted: boolean;
  notes: string | null;
  createdAt: string;
  site: {
    name: string;
    project: {
      name: string;
      city: { name: string };
    };
  };
  createdBy: { id: string; firstName: string; lastName: string };
  personnelEntries: Array<{
    id: string;
    dailyWage: number;
    overtimeHours: number | null;
    personnel: { id: string; firstName: string; lastName: string; title: string | null; employmentType?: string };
  }>;
  vehicleEntries: Array<{
    id: string;
    usageType: string;
    notes: string | null;
    km: number | null;
    hours: number | null;
    driverId: string | null;
    driverType: string | null;
    vehicle: { id: string; plate: string; brand: string | null; model: string | null; isMachinery?: boolean };
  }>;
  workEntries: Array<{
    id: string;
    quantity: number;
    notes: string | null;
    siteWork: {
      id: string;
      workType: { name: string; unit: string };
    };
  }>;
  photos: Array<{
    id: string;
    fileName: string;
    filePath: string;
    description: string | null;
  }>;
}

const DailyRecordDetail: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isAdmin = useIsAdmin();
  const isMobile = useIsMobile();
  const [record, setRecord] = useState<DailyRecord | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (id) {
      fetchRecord(id);
    }
  }, [id]);

  const fetchRecord = async (recordId: string) => {
    try {
      setLoading(true);
      const response = await dailyRecordsAPI.getOne(recordId);
      setRecord(response.data);
    } catch (err: any) {
      console.error('Record fetch error:', err);
      setError(err.response?.data?.message || 'Kayıt yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!record) return;
    if (!window.confirm('Bu günlük kaydı silmek istediğinizden emin misiniz? Bu işlem geri alınamaz.')) return;
    try {
      await dailyRecordsAPI.delete(record.id);
      toast.success('Günlük kayıt silindi');
      navigate('/daily-records');
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Silme başarısız');
    }
  };

  const getUsageTypeLabel = (type: string) => {
    switch (type) {
      case 'FULL_DAY':
        return 'Tam Gün';
      case 'HALF_DAY':
        return 'Yarım Gün';
      default:
        return type;
    }
  };

  const getDriverName = (entry: DailyRecord['vehicleEntries'][0]) => {
    if (!entry.driverId) return '-';
    // Sürücü bilgisi için personelden veya oluşturucu bilgisinden alabiliriz
    if (entry.driverType === 'USER') {
      return `${record?.createdBy.firstName} ${record?.createdBy.lastName} (Formen)`;
    }
    // Personnel için personel listesinden bul
    const personnel = record?.personnelEntries.find(
      (pe) => pe.personnel.id === entry.driverId
    );
    if (personnel) {
      return `${personnel.personnel.firstName} ${personnel.personnel.lastName}`;
    }
    return '-';
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner color="primary" />
        <p className="mt-2">Yükleniyor...</p>
      </div>
    );
  }

  if (error || !record) {
    return (
      <div className="text-center py-5">
        <i className="ri-error-warning-line text-danger" style={{ fontSize: '3rem' }}></i>
        <h5 className="mt-3">{error || 'Kayıt bulunamadı'}</h5>
        <button className="btn btn-primary mt-3" onClick={() => navigate('/daily-records')}>
          Listeye Dön
        </button>
      </div>
    );
  }

  return (
    <>
      <Row>
        <Col xs={12}>
          <div className="page-title-box d-flex align-items-center justify-content-between flex-wrap gap-2">
            <div className="d-flex align-items-center gap-3">
              <button
                className="btn btn-outline-secondary btn-icon"
                onClick={() => navigate('/daily-records')}
                title="Geri"
              >
                <i className="ri-arrow-left-line"></i>
              </button>
              <h4 className="mb-0">Günlük Kayıt Detayı</h4>
            </div>
            {isAdmin && record && (
              <Button color="danger" size="sm" outline onClick={handleDelete}>
                <i className="ri-delete-bin-line me-1"></i>
                Sil
              </Button>
            )}
          </div>
        </Col>
      </Row>

      {/* Özet Bilgiler */}
      <Row>
        <Col lg={12}>
          <Card>
            <CardHeader>
              <div className="d-flex align-items-center justify-content-between">
                <h5 className="card-title mb-0">
                  <i className="ri-calendar-line me-2"></i>
                  {new Date(record.recordDate).toLocaleDateString('tr-TR', {
                    weekday: 'long',
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                  })}
                </h5>
                <Badge color={record.isCompleted ? 'success' : 'warning'} className="fs-6">
                  {record.isCompleted ? 'Tamamlandı' : 'Devam Ediyor'}
                </Badge>
              </div>
            </CardHeader>
            <CardBody>
              <Row>
                <Col md={4}>
                  <p className="mb-1 text-muted">Şantiye</p>
                  <h6>
                    {record.site.project.city.name} / {record.site.project.name} / {record.site.name}
                  </h6>
                </Col>
                <Col md={4}>
                  <p className="mb-1 text-muted">Oluşturan</p>
                  <h6>{record.createdBy.firstName} {record.createdBy.lastName}</h6>
                </Col>
                <Col md={4}>
                  <p className="mb-1 text-muted">Kayıt Tarihi</p>
                  <h6>{new Date(record.createdAt).toLocaleString('tr-TR')}</h6>
                </Col>
              </Row>
              {record.notes && (
                <Row className="mt-3">
                  <Col>
                    <p className="mb-1 text-muted">Notlar</p>
                    <p className="mb-0">{record.notes}</p>
                  </Col>
                </Row>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>

      <Row>
        {/* Personel */}
        <Col lg={4}>
          <Card>
            <CardHeader>
              <h5 className="card-title mb-0">
                <i className="ri-team-line me-2 text-primary"></i>
                Personel
                <Badge color="primary" className="ms-2">{record.personnelEntries.length}</Badge>
              </h5>
            </CardHeader>
            <CardBody>
              {record.personnelEntries.length === 0 ? (
                <p className="text-muted text-center mb-0">Personel kaydı yok</p>
              ) : isMobile ? (
                // Mobile Card View
                <>
                  <div className="admin-card-list">
                    {record.personnelEntries.map((entry) => (
                      <div key={entry.id} className="admin-card-item">
                        <div className="admin-card-header">
                          <h6 className="admin-card-title mb-0">
                            {entry.personnel.firstName} {entry.personnel.lastName}
                          </h6>
                          <div className="d-flex gap-1">
                            <Badge color="info">{entry.dailyWage}</Badge>
                            {entry.overtimeHours && (
                              <Badge color="warning">{entry.overtimeHours} sa mesai</Badge>
                            )}
                          </div>
                        </div>
                        {entry.personnel.title && (
                          <div className="admin-card-body">
                            <div className="admin-info-row">
                              <span className="admin-info-label">
                                <i className="ri-user-star-line"></i>
                                Ünvan
                              </span>
                              <span className="admin-info-value">{entry.personnel.title}</span>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                  <div className="bg-light rounded p-2 mt-2 d-flex justify-content-between align-items-center">
                    <strong>Toplam</strong>
                    <strong>
                      {record.personnelEntries.reduce((sum, e) => sum + Number(e.dailyWage), 0)} yevmiye
                    </strong>
                  </div>
                </>
              ) : (
                // Desktop Table View
                <div className="table-responsive">
                  <Table className="table-sm mb-0">
                    <thead>
                      <tr>
                        <th>İsim</th>
                        <th className="text-end">Yevmiye</th>
                        <th className="text-end">Mesai</th>
                      </tr>
                    </thead>
                    <tbody>
                      {record.personnelEntries.map((entry) => (
                        <tr key={entry.id}>
                          <td>
                            <strong>{entry.personnel.firstName} {entry.personnel.lastName}</strong>
                            {entry.personnel.title && (
                              <small className="text-muted d-block">{entry.personnel.title}</small>
                            )}
                          </td>
                          <td className="text-end">
                            <Badge color="info">{entry.dailyWage}</Badge>
                          </td>
                          <td className="text-end">
                            {entry.overtimeHours ? (
                              <Badge color="warning">{entry.overtimeHours} saat</Badge>
                            ) : '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="table-light">
                        <td><strong>Toplam</strong></td>
                        <td className="text-end">
                          <strong>
                            {record.personnelEntries.reduce((sum, e) => sum + Number(e.dailyWage), 0)} yevmiye
                          </strong>
                        </td>
                        <td className="text-end">
                          {record.personnelEntries.reduce((sum, e) => sum + (e.overtimeHours || 0), 0) > 0 && (
                            <strong>
                              {record.personnelEntries.reduce((sum, e) => sum + (e.overtimeHours || 0), 0)} saat
                            </strong>
                          )}
                        </td>
                      </tr>
                    </tfoot>
                  </Table>
                </div>
              )}
            </CardBody>
          </Card>
        </Col>

        {/* Araçlar */}
        <Col lg={4}>
          <Card>
            <CardHeader>
              <h5 className="card-title mb-0">
                <i className="ri-truck-line me-2 text-success"></i>
                Araçlar
                <Badge color="success" className="ms-2">{record.vehicleEntries.length}</Badge>
              </h5>
            </CardHeader>
            <CardBody>
              {record.vehicleEntries.length === 0 ? (
                <p className="text-muted text-center mb-0">Araç kaydı yok</p>
              ) : isMobile ? (
                // Mobile Card View
                <div className="admin-card-list">
                  {record.vehicleEntries.map((entry) => (
                    <div key={entry.id} className="admin-card-item">
                      <div className="admin-card-header">
                        <h6 className="admin-card-title mb-0">{entry.vehicle.plate}</h6>
                        <Badge color={entry.usageType === 'FULL_DAY' ? 'primary' : 'secondary'}>
                          {getUsageTypeLabel(entry.usageType)}
                        </Badge>
                      </div>
                      <div className="admin-card-body">
                        {(entry.vehicle.brand || entry.vehicle.model) && (
                          <div className="admin-info-row">
                            <span className="admin-info-label">
                              <i className="ri-car-line"></i>
                              Model
                            </span>
                            <span className="admin-info-value">
                              {entry.vehicle.brand} {entry.vehicle.model}
                            </span>
                          </div>
                        )}
                        <div className="admin-info-row">
                          <span className="admin-info-label">
                            <i className="ri-user-line"></i>
                            Sürücü
                          </span>
                          <span className="admin-info-value">{getDriverName(entry)}</span>
                        </div>
                        {entry.km != null && (
                          <div className="admin-info-row">
                            <span className="admin-info-label">
                              <i className="ri-route-line"></i>
                              Km
                            </span>
                            <span className="admin-info-value">{entry.km} km</span>
                          </div>
                        )}
                        {entry.hours != null && (
                          <div className="admin-info-row">
                            <span className="admin-info-label">
                              <i className="ri-time-line"></i>
                              Saat
                            </span>
                            <span className="admin-info-value">{Number(entry.hours)} saat</span>
                          </div>
                        )}
                        {entry.notes && (
                          <div className="admin-info-row">
                            <span className="admin-info-label">
                              <i className="ri-file-text-line"></i>
                              Not
                            </span>
                            <span className="admin-info-value">{entry.notes}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                // Desktop Table View
                <div className="table-responsive">
                  <Table className="table-sm mb-0">
                    <thead>
                      <tr>
                        <th>Araç</th>
                        <th>Kullanım</th>
                        <th>Sürücü</th>
                        <th className="text-end">Km/Saat</th>
                      </tr>
                    </thead>
                    <tbody>
                      {record.vehicleEntries.map((entry) => (
                        <tr key={entry.id}>
                          <td>
                            <strong>{entry.vehicle.plate}</strong>
                            <small className="text-muted d-block">
                              {entry.vehicle.brand} {entry.vehicle.model}
                            </small>
                          </td>
                          <td>
                            <Badge color={entry.usageType === 'FULL_DAY' ? 'primary' : 'secondary'}>
                              {getUsageTypeLabel(entry.usageType)}
                            </Badge>
                          </td>
                          <td>
                            <small>{getDriverName(entry)}</small>
                          </td>
                          <td className="text-end">
                            {entry.km != null && <span>{entry.km} km</span>}
                            {entry.hours != null && <span>{Number(entry.hours)} saat</span>}
                            {entry.km == null && entry.hours == null && '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              )}
            </CardBody>
          </Card>
        </Col>

        {/* İşler */}
        <Col lg={4}>
          <Card>
            <CardHeader>
              <h5 className="card-title mb-0">
                <i className="ri-tools-line me-2 text-warning"></i>
                Yapılan İşler
                <Badge color="warning" className="ms-2">{record.workEntries.length}</Badge>
              </h5>
            </CardHeader>
            <CardBody>
              {record.workEntries.length === 0 ? (
                <p className="text-muted text-center mb-0">İş kaydı yok</p>
              ) : isMobile ? (
                // Mobile Card View
                <div className="admin-card-list">
                  {record.workEntries.map((entry) => (
                    <div key={entry.id} className="admin-card-item">
                      <div className="admin-card-header">
                        <h6 className="admin-card-title mb-0">{entry.siteWork.workType.name}</h6>
                        <Badge color="dark">
                          {Number(entry.quantity).toLocaleString('tr-TR')} {entry.siteWork.workType.unit}
                        </Badge>
                      </div>
                      {entry.notes && (
                        <div className="admin-card-body">
                          <div className="admin-info-row">
                            <span className="admin-info-label">
                              <i className="ri-file-text-line"></i>
                              Not
                            </span>
                            <span className="admin-info-value">{entry.notes}</span>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                // Desktop Table View
                <div className="table-responsive">
                  <Table className="table-sm mb-0">
                    <thead>
                      <tr>
                        <th>İş</th>
                        <th className="text-end">Miktar</th>
                      </tr>
                    </thead>
                    <tbody>
                      {record.workEntries.map((entry) => (
                        <tr key={entry.id}>
                          <td>
                            <strong>{entry.siteWork.workType.name}</strong>
                            {entry.notes && (
                              <small className="text-muted d-block">{entry.notes}</small>
                            )}
                          </td>
                          <td className="text-end">
                            <Badge color="dark">
                              {Number(entry.quantity).toLocaleString('tr-TR')} {entry.siteWork.workType.unit}
                            </Badge>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* Fotoğraflar */}
      {record.photos.length > 0 && (
        <Row>
          <Col lg={12}>
            <Card>
              <CardHeader>
                <h5 className="card-title mb-0">
                  <i className="ri-image-line me-2 text-info"></i>
                  Fotoğraflar
                  <Badge color="info" className="ms-2">{record.photos.length}</Badge>
                </h5>
              </CardHeader>
              <CardBody>
                <div className="row g-3">
                  {record.photos.map((photo) => (
                    <div key={photo.id} className="col-6 col-md-4 col-lg-3">
                      <div className="position-relative">
                        <AuthImage
                          src={photo.filePath}
                          alt={photo.description || photo.fileName}
                          className="img-fluid rounded"
                          style={{
                            width: '100%',
                            height: isMobile ? '120px' : '150px',
                            objectFit: 'cover',
                            cursor: 'pointer'
                          }}
                          onClick={() => openAuthFile(photo.filePath)}
                        />
                        {photo.description && (
                          <small className="d-block text-muted mt-1">{photo.description}</small>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>
      )}
    </>
  );
};

export default DailyRecordDetail;
